import React from 'react';
import { 
  Box, 
  Drawer, 
  List, 
  ListItemButton, 
  IconButton, 
  Divider, 
  TextField, 
  useMediaQuery, 
  Theme, 
  Typography,
  InputAdornment,
  useTheme,
  Button,
  Avatar,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArticleIcon from "@mui/icons-material/Article";
import ChatHeader from '../components/ChatHeader';
import ChatMessages from '../components/ChatMessages';
import ChatInput from '../components/ChatInput';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const drawerWidth = 300;

const ChatPage: React.FC = () => {
  const { conversations, activeConversationId, newConversation, selectConversation, deleteConversation } = useChat();
  const { user } = useAuth();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const theme = useTheme();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');

  const toggleMobile = () => setMobileOpen((prev) => !prev);

  // Function to highlight search terms in text
  const highlightSearchTerms = (text: string, searchTerm: string) => {
    if (!searchTerm.trim()) return text;
    
    const words = searchTerm.toLowerCase().split(/\s+/).filter(word => word.length > 0);
    let highlightedText = text;
    
    words.forEach(word => {
      const regex = new RegExp(`(${word})`, 'gi');
      highlightedText = highlightedText.replace(regex, `<mark style="background-color: ${theme.palette.warning.light}; padding: 0px 2px; border-radius: 2px; color: black;">$1</mark>`);
    });
    
    return highlightedText;
  };

  // Enhanced search function
  const searchConversations = (query: string) => {
    if (!query.trim()) return Object.values(conversations);
    
    const searchTerm = query.toLowerCase().trim();
    const searchWords = searchTerm.split(/\s+/).filter(word => word.length > 0);
    
    return Object.values(conversations).filter(conv => {
      // Search in title
      const titleMatch = conv.title.toLowerCase().includes(searchTerm);
      
      // Search in messages content
      const messageMatch = conv.messages.some(msg => {
        const content = msg.content.toLowerCase();
        // Check if all search words are present in the message
        return searchWords.every(word => content.includes(word));
      });
      
      // Search in message timestamps (for date-based searches)
      const dateMatch = searchTerm.match(/\d{1,2}[/-]\d{1,2}[/-]\d{2,4}/) && 
        conv.messages.some(msg => {
          const dateStr = msg.timestamp.toLocaleDateString();
          return dateStr.includes(searchTerm);
        });
      
      return titleMatch || messageMatch || dateMatch;
    });
  };

  // Filter conversations based on search
  const filteredConversations = searchConversations(searchQuery)
    .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime());

  // Check if current conversation has messages
  const activeConversation = conversations[activeConversationId || ''];
  const hasMessages = activeConversation?.messages && activeConversation.messages.length > 0;

  const sidebarContent = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      bgcolor: theme.palette.mode === 'dark' ? 'rgba(10, 10, 10, 0.95)' : '#f8fafc',
    }}>
      {/* Sidebar Header */}
      <Box sx={{ p: 3, pb: 2 }}>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={async () => await newConversation()}
          sx={{
            justifyContent: 'flex-start',
            py: 1.5,
            px: 2,
            borderRadius: 3,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)',
              boxShadow: '0 6px 16px rgba(99, 102, 241, 0.4)',
            }
          }}
        >
          New Chat
        </Button>
      </Box>
      
      {/* Navigation Links */}
      <Box sx={{ px: 2, mb: 2 }}>
        <ListItemButton
          onClick={() => navigate('/my-tickets')}
          sx={{
            borderRadius: 2,
            mb: 0.5,
            color: theme.palette.text.secondary,
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
              color: theme.palette.primary.main,
            }
          }}
        >
          <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
            <ConfirmationNumberIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText 
            primary="My Tickets" 
            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
          />
        </ListItemButton>
      </Box>

      <Box sx={{ px: 2, mb: 2 }}>
        <ListItemButton
        onClick={() => navigate('/document-builder')}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          color: theme.palette.text.secondary,
          '&:hover': {
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
          color: theme.palette.primary.main,
          }
        }}
        >
        <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
          <ArticleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText 
          primary="Document Builder" 
          primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
        />
        </ListItemButton>
      </Box>


    <Box sx={{ px: 2, mb: 2 }}>
      <ListItemButton
      onClick={() => navigate('/business-info')}
      sx={{
        borderRadius: 2,
        mb: 0.5,
        color: theme.palette.text.secondary,
        '&:hover': {
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
        color: theme.palette.primary.main,
        }
      }}
      >
      <ListItemIcon sx={{ minWidth: 32, color: 'inherit' }}>
        <ArticleIcon fontSize="small" />
      </ListItemIcon>
      <ListItemText 
        primary="Business Info" 
        primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }}
      />
      </ListItemButton>
    </Box>

      {/* Search */}
      <Box sx={{ px: 2, mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search chats..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#ffffff',
              '& fieldset': {
                border: '1px solid',
                borderColor: theme.palette.divider,
              },
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
              }
            }
          }}
        />
      </Box>
      
      <Box sx={{ px: 3, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.text.secondary, letterSpacing: '0.05em' }}>
          HISTORY
        </Typography>
        {searchQuery && (
          <Typography variant="caption" color="primary">
            {filteredConversations.length} found
          </Typography>
        )}
      </Box>
      
      {/* Chat List */}
      <List sx={{ 
        overflowY: 'auto', 
        flex: 1, 
        px: 2,
        '&::-webkit-scrollbar': { width: '4px' },
        '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
      }}>
        {filteredConversations.map((conv) => (
          <ListItemButton 
            key={conv.id} 
            selected={conv.id === activeConversationId} 
            onClick={() => {
              selectConversation(conv.id);
              if (isMobile) setMobileOpen(false);
            }}
            sx={{ 
              borderRadius: 2,
              mb: 0.5,
              py: 1.5,
              position: 'relative',
              '&.Mui-selected': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.08)',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.25)' : 'rgba(99, 102, 241, 0.12)',
                },
                '& .delete-btn': { opacity: 1 }
              },
              '&:hover .delete-btn': { opacity: 1 }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', overflow: 'hidden' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: conv.id === activeConversationId ? 600 : 500,
                    color: conv.id === activeConversationId ? theme.palette.primary.main : theme.palette.text.primary,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    mr: 1
                  }}
                >
                  {conv.title}
                </Typography>
              </Box>
              
              <Typography 
                variant="caption" 
                sx={{ 
                  color: theme.palette.text.secondary,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block'
                }}
                dangerouslySetInnerHTML={{
                  __html: highlightSearchTerms(
                    conv.messages[conv.messages.length - 1]?.content?.slice(0, 60) || 'New chat',
                    searchQuery
                  )
                }}
              />
            </Box>

            <IconButton 
              size="small" 
              className="delete-btn"
              onClick={async (e) => {
                e.stopPropagation(); 
                await deleteConversation(conv.id);
              }}
              sx={{ 
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s',
                color: theme.palette.text.secondary,
                bgcolor: theme.palette.background.paper,
                boxShadow: 1,
                width: 24,
                height: 24,
                '&:hover': { 
                  color: theme.palette.error.main,
                  bgcolor: theme.palette.background.paper
                }
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </ListItemButton>
        ))}
      </List>
      
      {/* User Footer */}
      <Divider sx={{ opacity: 0.5 }} />
      <Box sx={{ p: 2 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1.5,
          p: 1,
          borderRadius: 2,
          '&:hover': { bgcolor: theme.palette.action.hover, cursor: 'pointer' }
        }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: theme.palette.primary.main }}>
            {user?.email?.charAt(0).toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user?.email || 'User Account'}
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
              {user?.org_slug || 'Organization'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      height: '100vh', 
      width: '100vw', 
      overflow: 'hidden', 
      background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0f0f0f 100%)'
        : 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 50%, #e2e8f0 100%)',
    }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleMobile}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {sidebarContent}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: theme.palette.divider,
              boxShadow: '4px 0 24px rgba(0,0,0,0.02)'
            },
          }}
          open
        >
          {sidebarContent}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Header */}
        <ChatHeader />
        
        {/* Messages Area */}
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {!hasMessages && (
            <Box sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -60%)',
              textAlign: 'center',
              zIndex: 0,
              width: '100%',
              maxWidth: 600,
              px: 3
            }}>
              <Box sx={{
                width: 64,
                height: 64,
                borderRadius: '24px',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)'
              }}>
                <AutoAwesomeIcon sx={{ color: 'white', fontSize: 32 }} />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, color: theme.palette.text.primary }}>
                How can I help you today?
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
                I can help you resolve tickets, find information, or assist with daily tasks.
              </Typography>
              
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                {[
                  { icon: <ConfirmationNumberIcon />, text: "Check status of ticket #1234" },
                  { icon: <SearchIcon />, text: "Find policy on remote work" },
                  { icon: <ChatBubbleOutlineIcon />, text: "Draft a response to a customer" },
                  { icon: <AutoAwesomeIcon />, text: "Summarize my recent tickets" }
                ].map((item, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    startIcon={item.icon}
                    onClick={() => document.dispatchEvent(new CustomEvent('insertPrompt', { detail: item.text }))}
                    sx={{
                      justifyContent: 'flex-start',
                      py: 1.5,
                      px: 2,
                      borderRadius: 3,
                      borderColor: theme.palette.divider,
                      color: theme.palette.text.primary,
                      bgcolor: theme.palette.background.paper,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.1)' : 'rgba(99, 102, 241, 0.05)',
                      }
                    }}
                  >
                    {item.text}
                  </Button>
                ))}
              </Box>
            </Box>
          )}

          <Box sx={{ 
            flex: 1, 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            pt: hasMessages ? 2 : 0 
          }}>
            <ChatMessages />
          </Box>

          <Box sx={{ 
            p: 3, 
            pt: 1,
            width: '100%', 
            maxWidth: 900, 
            mx: 'auto',
            position: 'relative',
            zIndex: 10
          }}>
            <ChatInput />
            <Typography variant="caption" sx={{ 
              display: 'block', 
              textAlign: 'center', 
              mt: 1.5, 
              color: theme.palette.text.disabled,
              fontSize: '0.75rem'
            }}>
              CoreSupport AI can make mistakes. Verify important information.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatPage;
